/**
 * String cleanup shared by every parser.
 *
 * Israeli statements are full of bidi control characters — the Isracard export
 * wraps the card line in U+202B/U+202C, and merchant cells carry stray marks.
 * They are invisible, so they silently break equality checks, dedupe keys and
 * substring rules. Everything gets stripped at the parse boundary, once.
 */

/** ZWSP/ZWNJ/ZWJ, LRM/RLM, LRE..RLO+PDF, isolates, and the BOM. */
const BIDI_AND_INVISIBLE = /[​-‏‪-‮⁦-⁩﻿]/g;

/** Any whitespace including NBSP and the narrow NBSP. */
const ANY_SPACE = /[\s  ]+/g;

export function stripBidi(value: string): string {
  return value.replace(BIDI_AND_INVISIBLE, '');
}

/**
 * Canonical whitespace: bidi stripped, NBSP folded, runs collapsed, trimmed.
 * Newlines become spaces — the Isracard details column is multi-line
 * ("אתר חו\"ל\nהוראת קבע") and we want it as one comparable string.
 */
export function cleanCell(value: string): string {
  return stripBidi(value).replace(ANY_SPACE, ' ').trim();
}

/** cleanCell, but line breaks survive for values we display verbatim. */
export function cleanMultiline(value: string): string {
  return stripBidi(value)
    .split(/\r?\n/)
    .map((line) => line.replace(ANY_SPACE, ' ').trim())
    .filter((line) => line.length > 0)
    .join('\n');
}

const HEBREW = /[֐-׿]/;

export function containsHebrew(value: string): boolean {
  return HEBREW.test(value);
}

/**
 * Branch suffixes that appear appended to a merchant name. Removing them merges
 * "רמי לוי- קדימה" with "רמי לוי" so rules and merchant totals behave.
 */
const CITY_SUFFIXES = [
  'תל אביב', 'ת"א', 'ירושלים', 'חיפה', 'נתניה', 'רעננה', 'הרצליה', 'כפר סבא',
  'ראשון לציון', 'פתח תקווה', 'באר שבע', 'אשדוד', 'רמת גן', 'קדימה', 'צור משה',
  'עין ורד', 'רחובות', 'חולון', 'בת ים', 'אשקלון', 'מודיעין',
];

/**
 * Merchant key used for dedupe, rule matching and grouping.
 *
 * Deliberately conservative: it folds formatting noise (case, padding, bidi
 * marks, punctuation, trailing branch numbers) but keeps the merchant's
 * identity. Over-normalizing merges genuinely different merchants, which is far
 * harder to notice after the fact than failing to merge two spellings of one.
 */
export function normalizeMerchant(raw: string): string {
  let value = cleanCell(raw);
  if (value.length === 0) return '';

  // Latin merchants arrive space-padded to a fixed width ("NETFLIX.COM      ").
  value = value.toUpperCase();

  // Separator punctuation. Apostrophes and & stay: they are inside real names
  // ("איירון ג'ים", "AT&T").
  value = value.replace(/[*\-–—_.,/\\|]+/g, ' ');

  // Trailing branch/store number: "רמי לוי 123", "SHUFERSAL 0042".
  value = value.replace(/\s+\d{1,5}$/, '');

  value = value.replace(ANY_SPACE, ' ').trim();

  for (const city of CITY_SUFFIXES) {
    const suffix = ` ${city.toUpperCase()}`;
    if (value.endsWith(suffix)) {
      value = value.slice(0, -suffix.length).trim();
      break;
    }
  }

  return value;
}
